import React, { JSX } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { BackstoryTextField, BackstoryTextFieldRef } from 'components/BackstoryTextField';
import { DeleteConfirmation } from 'components/DeleteConfirmation';
import { ChatSession } from 'types/types';

interface ChatComposerProps {
  chatSession: ChatSession | null;
  streaming: boolean;
  loading: boolean;
  isMobile: boolean;
  backstoryTextRef: React.RefObject<BackstoryTextFieldRef | null>;
  onDelete: (session: ChatSession) => Promise<void>;
  onSendMessage: (message: string) => Promise<void>;
}

function ChatComposer({
  chatSession,
  streaming,
  loading,
  isMobile,
  backstoryTextRef,
  onDelete,
  onSendMessage,
}: ChatComposerProps): JSX.Element {
  const disabled = streaming || loading;
  return (
    <Box sx={isMobile ? mobileComposerSx : desktopComposerSx}>
      <DeleteConfirmation
        onDelete={(): void => {
          chatSession && onDelete(chatSession);
        }}
        disabled={!chatSession}
        size={isMobile ? 'small' : 'large'}
        sx={isMobile ? mobileResetSx : desktopResetSx}
        action="reset"
        label="chat session"
        title="Reset Chat Session"
        message="Are you sure you want to reset the session? This action cannot be undone."
      />
      <BackstoryTextField
        placeholder={
          isMobile ? 'Ask about the candidate...' : 'Type your message about the candidate...'
        }
        ref={backstoryTextRef}
        onEnter={onSendMessage}
        disabled={disabled}
        sx={isMobile ? mobileTextFieldSx : desktopTextFieldSx}
        style={isMobile ? mobileTextFieldStyle : undefined}
      />
      <Tooltip title="Send">
        <span style={{ minWidth: 'auto', maxHeight: 'min-content', alignSelf: 'flex-end' }}>
          <Button
            variant="contained"
            disabled={disabled}
            sx={isMobile ? mobileSendButtonSx : undefined}
            onClick={(): void => {
              void onSendMessage(backstoryTextRef.current?.getAndResetValue() || '');
            }}
          >
            <SendIcon />
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

const desktopComposerSx = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 1,
  flexShrink: 0,
  px: 0,
  py: 0,
  backgroundColor: 'background.paper',
};
const mobileComposerSx = {
  ...desktopComposerSx,
  gap: 0.5,
  px: 0.75,
  py: 0.5,
  boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.08)',
};
const desktopResetSx = { minWidth: 'auto', px: 2, maxHeight: 'min-content' };
const mobileResetSx = { minWidth: 'auto', width: 34, height: 34, p: 0.5, maxHeight: 'min-content' };
const desktopTextFieldSx = { p: 1, minWidth: 0 };
const mobileTextFieldSx = { p: 0, minWidth: 0 };
const mobileTextFieldStyle: React.CSSProperties = {
  minHeight: 36,
  maxHeight: 84,
  padding: '8px 10px',
  lineHeight: '1.25',
  fontSize: '15px',
};
const mobileSendButtonSx = { minWidth: 42, height: 36, px: 1 };

export { ChatComposer };
